<script setup lang="ts">
import { ref, watch, nextTick } from "vue";

import type { Headers, PaginationMeta } from "@/types/rest";
import type {
  ConfiguredEntity,
  IntegrationInstance,
  EntityFilterData
} from "@/types/integrationInstance";

import type { Group } from "@/types/group";
import type { IncludedEntity } from "@/types/activity";
import type { ChangeCallbackParams } from "@/types/config";

import { useComponentErrorCapture } from "@/mixins/error";
import { getPaginationLimit, savePaginationLimit } from "@/mixins/listing";
import { isTouchEnabled } from "@/mixins/device";

import { integrationsStore } from "@/stores/integrations";
import { profileStore } from "@/stores/profile";
import { addErrorBottom } from "@/stores/messages";

import Modal from "@/components/elements/Modal.vue";
import IconSelect from "@/components/elements/icon/IconSelect.vue";
import UCInput from "@/components/ui/UCInput.vue";
import IncludedEntities from "@/components/elements/entity/IncludedEntities.vue";
import EntityListFiltered from "@/components/elements/entity/EntityListFiltered.vue";

const integrationStorage = integrationsStore();

useComponentErrorCapture();

defineExpose({
  open
});

const props = defineProps({
  group: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(["close", "saved"]);

const entityListFilter = ref(<EntityFilterData>{
  searchText: '',
  entityTypes: '',
  instances: ''
});

const profileStorage = profileStore();

const activeGroup = ref<Group>(JSON.parse(JSON.stringify(props.group)));
const values = ref<Record<string, any>>({});
const allEntities = ref<ConfiguredEntity[]>([]);
const filteredEntities = ref<ConfiguredEntity[]>([]);
const selectedEntities = ref<IncludedEntity[]>([]);
const instances = ref<IntegrationInstance[]>([]);

const showModal = ref(false);
const showAvailableEntities = ref(false);

const pagination = ref<PaginationMeta>({
  limit: getPaginationLimit() ?? 20,
  page: 1,
});

const loading = ref(false);

const groupAvailableEntities = ref();

watch(showModal, async (val) => {
  activeGroup.value = JSON.parse(JSON.stringify(props.group));
  if (val == true) {
    setDefaults();
    allEntities.value = integrationStorage.$state.configuredEntities;
    fetchFilteredEntities(true);
  }
})

watch(activeGroup, () => {
  if (activeGroup.value) {
    values.value = {
      icon: activeGroup.value.icon || 'uc:profile',
      name: activeGroup.value.name,
      description: activeGroup.value.description || '',
    };
  }
});

watch(
  () => pagination.value.limit,
  (val) => {
    savePaginationLimit(val);
  }
);

watch(showAvailableEntities, (newVal) => {
  if (newVal) {
    nextTick(() => {
      const container = groupAvailableEntities.value as HTMLElement;
      if (!isTouchEnabled() && container) {
        const firstInput = container.querySelector('input') as HTMLElement;
        firstInput?.focus();
      }
    });
  }
});

integrationStorage.$subscribe((mutation, state) => {
  if (state.instances) {
    instances.value = state.instances;
  }
});

async function setDefaults() {
  loading.value = true;
  try {
    instances.value = await integrationStorage.getInstances();
  } catch (e: any) {
    addErrorBottom(e);
  }
  selectedEntities.value = getGroupEntities(activeGroup.value);
  loading.value = false;
}

function getGroupEntities(group: Group): ConfiguredEntity[] {
  return group.entities.map((entity_id: string) => {
    return allEntities.value.find((entity: ConfiguredEntity) => {
      return entity.entity_id === entity_id;
    }) as ConfiguredEntity;
  });
}

function changeFilter(data: EntityFilterData) {
  entityListFilter.value = data;
  fetchFilteredEntities(true);
}

function reloadEntities() {
  setDefaults();
}

async function fetchFilteredEntities(userFetchFirstPage: boolean = false) {
  if (userFetchFirstPage === true) {
    pagination.value.page = 1;
  }

  let searchText = entityListFilter.value.searchText;

  try {
    const entList = await integrationStorage.getConfiguredEntitiesByPageByLimit(entityListFilter.value.instances || '', false, pagination.value.page, pagination.value.limit, searchText, entityListFilter.value.entityTypes, props.group.group_id);
    filteredEntities.value = entList.data.configuredEntities as ConfiguredEntity[];

    const listHeaders = entList.headers as Headers;
    if (listHeaders) {
      const headerLimit = Number(listHeaders['pagination-limit']);
      const newLimit = headerLimit <= Number(listHeaders['pagination-count']) ? pagination.value.limit : headerLimit;
      pagination.value = {
        count: Number(listHeaders['pagination-count']) || 0,
        limit: newLimit || 0,
        page: Number(listHeaders['pagination-page']) || 0,
      }
    }
  } catch (e) {
    console.error(e);
  }
}

function open() {
  showModal.value = true;
}

function closeModal() {
  showModal.value = false;
}

async function changeGroupIcon(params: ChangeCallbackParams) {
  const { value } = params;

  const newValues = {
    ...activeGroup.value,
    icon: value as string,
  } as Group;

  submitChange(newValues);
}

function changeItemName(name: string) {
  const newValues = {
    ...activeGroup.value,
    name: name,
  } as Group;

  submitChange(newValues);
}

function changeItemDescription(description: string) {
  const newValues = {
    ...activeGroup.value,
    description: description,
  } as Group;

  submitChange(newValues);
}

async function submitChange(message: Group) {
  if (!message) {
    return;
  }

  try {
    const mess = await profileStorage.updateGroup(message)
    await fetchFilteredEntities(true);
    setNewData(mess);
    emit("saved", activeGroup.value);
  } catch (e: any) {    
    addErrorBottom(e, 'group.update');
  }
}

async function entityListChanged(newList: IncludedEntity[]) {
  selectedEntities.value = newList;
  const newValues = {
    ...activeGroup.value,
    entities: selectedEntities.value.map((entity) => {
      return entity.entity_id;
    }),
  } as Group;

  submitChange(newValues);
}

function setNewData(groupList: Group[]) {
  const updatedGroup = groupList.find(g => g.group_id == activeGroup.value.group_id)
  if (updatedGroup) {
    activeGroup.value = JSON.parse(JSON.stringify(updatedGroup));
  }
}

function addEntitiesToGroup(entities: ConfiguredEntity[]) {
  const newList = JSON.parse(JSON.stringify( selectedEntities.value)).concat(entities);
  entityListChanged(newList);
}

function changePage(value: number) {
  pagination.value.page = value;
  fetchFilteredEntities();
}

function changePerPage(value: number) {
  pagination.value.page = 1;
  pagination.value.limit = value;
  fetchFilteredEntities();
}

</script>
<template>
  <Teleport to="body">
    <Modal
      :show="showModal"
      :height="'100%'"
      :cols="true"
      @close="closeModal"
      name="edit-group"
      class="modal--edit-group">
      <template #header>
        {{ $t("group.edit.edit_group") }}
      </template>
      <div class="modal__body__col modal__body__col-50 modal__body__col--meta">
        <IconSelect
          :key="activeGroup.icon ? activeGroup.icon : 'fa-light fa-layer-group'"
          :value="activeGroup.icon ? activeGroup.icon : 'fa-light fa-layer-group'"
          :fallback="'fa-light fa-layer-group'"
          :change-callback="changeGroupIcon"
        />
        <UCInput
          v-model="values.name"
          @submit="changeItemName"
          :full-w="true"
          :label="$t('form.name')"
        />
        <UCInput
          v-model="values.description"
          @submit="changeItemDescription"
          :type="'textarea'"
          :full-w="true"
          :label="$t('form.description')"
        />
        <p class="modal--edit-group__meta-info">{{ $t("group.edit.drag_instructions") }}</p>
        <Transition :name="showAvailableEntities == true ? 'slide-tab-right' : 'slide-tab-left'">
          <div
            v-show="showAvailableEntities == true"
            ref="groupAvailableEntities"
            class="modal--edit-group__available-entities"
          >
            <div class="modal--edit-group__available-entities__header">
              <button  @click="showAvailableEntities = false" class="button button--secondary button--icon">
                <i class="fa-light fa-arrow-left"></i>
              </button>
              <span>{{ $t("group.edit.add_entities") }}</span>
            </div>
            <EntityListFiltered
              @add-entities="addEntitiesToGroup"
              @change-filter="changeFilter"
              @reload-entities="reloadEntities"
              @change-page="changePage"
              @change-per-page="changePerPage"
              :pagination="pagination"
              :all-entities="filteredEntities"
              :instances="instances"
              :drag-group="'group-entities'"
              :has-quick-options="true"
              :parent="'edit-group'"
            />
          </div>
        </Transition>
      </div>
      <div class="modal__body__col modal__body__col-50">
        <IncludedEntities
          ref="includedList"
          @entity-list-changed="entityListChanged"
          @reload-entities="reloadEntities"
          @click-add="showAvailableEntities = true"
          :entities="selectedEntities"
          :instances="instances"
          :show-button-add="showAvailableEntities == false"
          :drag-group="'group-entities'"
          :drag-button="true"
          :loading="loading"
          :text-add-first-descr="$t('group.edit.empty') + ' ' + $t('entity.add_first_btn_below')"
        />
      </div>
    </Modal>
  </Teleport>
</template>